import { toast } from 'vue-sonner'
import { useDeepBoxDownload } from '@deepcloud/deep-ui-kit'
import { useKeycloak } from '@/composables/auth/keycloak.ts'
import type { DownloadZipTaskItem } from '@/components/node/NodeTasksZipDownloadCard.vue'
import type { Node } from '@deepcloud/api-deep-box'
import type { ZipDownloadError } from '@deepcloud/deep-ui-kit'

const showDownloadZipSnackbar = ref(false)
const downloadZipItems = ref(new Set<DownloadZipTaskItem>())

export function useDownload(includeHidden: MaybeRefOrGetter<boolean> = false) {
  const { t } = useI18n()

  const { bearer } = useKeycloak()
  const env = import.meta.env.VITE_ENVIRONMENT

  const {
    downloadSingleNode,
    downloadMultipleNodes,
    getDownloadUrl,
    getDownloadUrlForPreview,
  } = useDeepBoxDownload(
    computed(() => bearer.value ?? ''),
    env,
  )

  watchEffect(() => {
    showDownloadZipSnackbar.value = downloadZipItems.value.size > 0
  })

  function startMultipleDownload(nodes: Node[]) {
    const abortController = new AbortController()

    const progress = ref<number>(0)
    const error = ref<ZipDownloadError>()

    const zipDownloadTask: DownloadZipTaskItem = {
      nodes,
      abortController,
      getProgress: () => progress.value,
      getError: () => error.value,
    }

    void downloadMultipleNodes(
      nodes.map((i) => i.nodeId),
      {
        onRequested() {
          downloadZipItems.value.add(zipDownloadTask)
        },
        onProgress(progressValue) {
          if (
            progressValue > 0 &&
            progress.value !== progressValue &&
            error.value
          ) {
            error.value = undefined
          }
          progress.value = progressValue
        },
        onReady() {
          error.value = undefined
          progress.value = 100
        },
        onError(e: ZipDownloadError) {
          error.value = e
        },
        onAbort() {
          downloadZipItems.value.delete(zipDownloadTask)
        },
        signal: abortController.signal,
        includeHidden: toValue(includeHidden),
      },
    )
  }

  function onDownloadNodes(nodes: Node[]) {
    if (nodes.length === 0) return

    if (nodes.length === 1 && nodes[0].type === 'file') {
      void downloadSingleNode(nodes[0])
    } else {
      if (nodes.length > 100) {
        toast.error(t('error.download.multiple.n_max_length', 100))
        return
      }
      void startMultipleDownload(nodes)
    }
  }

  return {
    downloadSingleNode,
    onDownloadNodes,
    getDownloadUrlForPreview,
    getDownloadUrl,
    showDownloadZipSnackbar,
    downloadZipItems,
  }
}
